import type { JSX, PointerEvent as ReactPointerEvent } from "react";
import { useMemo, useRef, useState } from "react";
import { axisTicks } from "./timeAxis";
import { niceCeil, Y_TICK_FRACTIONS } from "./geometry";

export interface TimePoint { ts: number; value: number; }

/**
 * Time-navigable trend line with a faint area fill (#228 Phase 3). Monochrome by
 * default (design-system Data Viz: no brand flood); a caller may pass a semantic
 * `stroke` (e.g. the error rate uses --color-error) when the color conveys
 * meaning.
 *
 * When `from`/`to` (epoch ms) are supplied the chart draws a real date+time axis
 * (adaptive tick granularity, via timeAxis.ts) and x maps to wall-clock time
 * rather than point index, so gaps in the series read as gaps in time. The axis
 * labels are HTML (not SVG <text>) because the SVG is stretched with
 * preserveAspectRatio="none" — text would be squished. Passing `onZoom` enables a
 * drag-to-brush gesture: the user drags a region and the chart reports the
 * selected sub-window so the caller can re-query it (zoom in). Pan is driven by
 * the from/to the caller passes (see the overview slider in the hosting section).
 */
export function LineChart({
  points, height = 140, stroke = "var(--cc-viz-line)", from, to, onZoom, formatY
}: {
  points: TimePoint[];
  height?: number;
  stroke?: string;
  from?: number;
  to?: number;
  onZoom?: (from: number, to: number) => void;
  /** Format a y-axis value in its unit (tokens / ms / ¥ / %). Default: raw number. */
  formatY?: (n: number) => string;
}): JSX.Element {
  const w = 600;
  const hasAxis = from !== undefined && to !== undefined && to > from;
  const brushable = hasAxis && onZoom !== undefined;
  const fmtY = formatY ?? ((n: number) => String(Math.round(n)));

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [brush, setBrush] = useState<{ x0: number; x1: number } | null>(null);

  // Derive the SVG paths + axis ticks once per (points, window, height) change —
  // InsightsSection renders several charts, so an unrelated parent re-render
  // (theme/live toggle) should not recompute every path string. Computed before
  // the empty-points early return so the hook order stays stable. `ceil` is a
  // "nice" rounded max so the y-axis shows clean numbers and the line never
  // touches the top; yTicks are the [max, mid, 0] grid values.
  const geom = useMemo(() => {
    if (points.length === 0) return null;
    const ceil = niceCeil(Math.max(1, ...points.map((p) => p.value)));
    const y = (v: number): number => Math.round(height - (v / ceil) * (height - 4) - 2);
    // x (in viewBox units) maps to time when an axis window is given, else to index.
    const xOf = (p: TimePoint, i: number): number => {
      if (hasAxis) return ((p.ts - from) / (to - from)) * w;
      const step = points.length > 1 ? w / (points.length - 1) : w;
      return i * step;
    };
    const pts = points.map((p, i) => `${Math.round(xOf(p, i))} ${y(p.value)}`);
    const line = `M ${pts.join(" L ")}`;
    const firstX = Math.round(xOf(points[0]!, 0));
    const lastX = Math.round(xOf(points[points.length - 1]!, points.length - 1));
    const area = `${line} L ${lastX} ${height} L ${firstX} ${height} Z`;
    const ticks = hasAxis ? axisTicks(from, to) : [];
    const yTicks = Y_TICK_FRACTIONS.map((f) => f * ceil).map((v) => ({ v, y: y(v) }));
    return { line, area, ticks, yTicks };
  }, [points, height, from, to, hasAxis]);

  if (geom === null) return <div className="cc-empty">暂无数据</div>;

  const { line, area, ticks, yTicks } = geom;
  const fill = `color-mix(in srgb, ${stroke} 9%, transparent)`;
  const tickPct = (ts: number): number => hasAxis ? ((ts - from) / (to - from)) * 100 : 0;

  // Brush: translate a pointer x (CSS px) → fraction of the plot → timestamp, so
  // the reported window is real wall-clock time regardless of the rendered width.
  const toFrac = (clientX: number): number => {
    const el = svgRef.current;
    if (el === null) return 0;
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  };
  const fracToTs = (f: number): number => (hasAxis ? Math.round(from + f * (to - from)) : 0);

  const onDown = (e: ReactPointerEvent<SVGSVGElement>): void => {
    if (!brushable) return;
    const f = toFrac(e.clientX);
    setBrush({ x0: f, x1: f });
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent<SVGSVGElement>): void => {
    if (brush === null) return;
    setBrush({ x0: brush.x0, x1: toFrac(e.clientX) });
  };
  const onUp = (): void => {
    if (brush === null) { return; }
    const lo = Math.min(brush.x0, brush.x1);
    const hi = Math.max(brush.x0, brush.x1);
    setBrush(null);
    // Ignore an accidental click (a near-zero drag) — require a real selection.
    if (hi - lo < 0.01 || onZoom === undefined) return;
    const a = fracToTs(lo);
    const b = fracToTs(hi);
    if (b > a) onZoom(a, b);
  };

  return (
    <div className="cc-chart">
      <div className="cc-chart-main">
        {/* Y-axis value labels (HTML overlay — the SVG is stretched with
            preserveAspectRatio="none", so in-SVG text would be squished). */}
        <div className="cc-yaxis" style={{ height }} aria-hidden="true">
          {yTicks.map((t) => (
            <span key={t.v} className="cc-yaxis-label" style={{ top: `${(t.y / height) * 100}%` }}>{fmtY(t.v)}</span>
          ))}
        </div>
        <div className="cc-plot">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${w} ${height}`}
            preserveAspectRatio="none"
            className={`cc-linechart${brushable ? " cc-linechart-brushable" : ""}`}
            style={{ height }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
          >
            {/* Horizontal y gridlines at the tick values. */}
            {yTicks.map((t) => (
              <line key={`y${t.v}`} x1={0} y1={t.y} x2={w} y2={t.y} className="cc-axis-grid" vectorEffect="non-scaling-stroke" />
            ))}
            {hasAxis && ticks.map((t) => (
              <line key={t.ts} x1={(tickPct(t.ts) / 100) * w} y1={0} x2={(tickPct(t.ts) / 100) * w} y2={height} className="cc-axis-grid" vectorEffect="non-scaling-stroke" />
            ))}
            <path d={area} fill={fill} stroke="none" />
            <path d={line} fill="none" stroke={stroke} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            {brush !== null && (
              <rect className="cc-brush" x={Math.min(brush.x0, brush.x1) * w} y={0} width={Math.abs(brush.x1 - brush.x0) * w} height={height} />
            )}
          </svg>
          {hasAxis && (
            <div className="cc-axis-row" aria-hidden="true">
              {ticks.map((t) => (
                <span key={t.ts} className="cc-axis-label" style={{ left: `${tickPct(t.ts)}%` }}>{t.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
